import React, { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  size: number;
  speed: number;
  opacity: number;
  wobbleSpeed: number;
  wobbleAmount: number;
  wobbleOffset: number;
}

export default function BackgroundParticles() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let particles: Particle[] = [];
    const particleCount = 20;

    // Resize canvas
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initialize particles
    const initParticles = () => {
      particles = [];
      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height + canvas.height, // start off or randomly distributed
          size: Math.random() * 4 + 2, // 2px to 6px
          speed: Math.random() * 0.4 + 0.1, // 0.1 to 0.5 px/frame
          opacity: Math.random() * 0.15 + 0.05, // 5% to 20% opacity
          wobbleSpeed: Math.random() * 0.02 + 0.005,
          wobbleAmount: Math.random() * 20 + 5,
          wobbleOffset: Math.random() * 100
        });
      }
    };
    initParticles();

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p) => {
        // Move particle upwards
        p.y -= p.speed;
        p.wobbleOffset += p.wobbleSpeed;

        // Apply a gentle side-to-side wobble
        const currentX = p.x + Math.sin(p.wobbleOffset) * p.wobbleAmount;

        // Draw particle
        ctx.beginPath();
        const gradient = ctx.createRadialGradient(
          currentX, p.y, 0,
          currentX, p.y, p.size * 2
        );
        gradient.addColorStop(0, `rgba(251, 191, 36, ${p.opacity})`); // gold/yellow-400
        gradient.addColorStop(0.5, `rgba(245, 158, 11, ${p.opacity * 0.5})`); // amber-500
        gradient.addColorStop(1, 'rgba(251, 191, 36, 0)');
        
        ctx.fillStyle = gradient;
        ctx.arc(currentX, p.y, p.size * 2, 0, Math.PI * 2);
        ctx.fill();

        // Reset particle if it goes off screen
        if (p.y < -p.size * 3) {
          p.y = canvas.height + Math.random() * 50;
          p.x = Math.random() * canvas.width;
          p.opacity = Math.random() * 0.15 + 0.05;
          p.size = Math.random() * 4 + 2;
          p.speed = Math.random() * 0.4 + 0.1;
        }
      });

      animationFrameId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none z-0"
      style={{ mixBlendMode: 'screen' }}
    />
  );
}
